import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { isEmpty } from 'lodash';

import ConditionReport from '@sm360/condition-report';
import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { Quote } from '@sm360/widget-commons-vehicle';
import {
    AccountService,
    CONSTANT,
    EVENTS,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    TRACKING_TAGS as TAGS,
    ShiftDigitalService,
} from '@sm360/widget-commons-general';
import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';
import { DatePicker } from '@sm360/widget-commons-forms';

import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import VehicleTypeLabel from 'trade-in-360/components/InstantEstimateWrapper/Components/VehicleTypeLabel/VehicleTypeLabel.jsx';

import 'trade-in-360/components/InstantEstimateWrapper/Steps/AppointmentFormTrade360/AppointmentFormTrade360.scss';
import TradeInCta from 'trade-in-360/components/TradeInCta/TradeInCta.jsx';
import ErrorMessage from 'trade-in-360/components/ErrorMessage/ErrorMessage.jsx';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import { logger } from '@sm360/logger';

import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import ConfirmationMessageTrade360 from '../../Components/ConfirmationMessageTrade360/ConfirmationMessageTrade360.jsx';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import TimePicker from './components/TimePicker/TimePicker.jsx';
import CONFIRMATION_TYPE from '../../../../constants/confirmationType.js';

const { QuestionsListHelpers, Models } = ConditionReport;
const { OFFICIAL_FORM_STATUS, APPOINTMENT_DATE_FORMAT } = CONSTANT || {};

const APPOINTMENTS_STARTING_TIME = 9;
const APPOINTMENTS_ENDING_TIME = 19;

const AppointmentFormTrade360 = ({
    tradeInVehicle,
    quote = undefined,
    onPreviousStep,
    onNextStep,
    closeText,
    onClose,
    accountTradeInVehicle,
    onOfficialEvaluation,
    saveTradeIn,
    onChange,
    isShowroomV2Enabled,
    config,
}) => {
    const { fieldsLocked, setFieldsLocked, vehicleInventoryId, catalogVehicleId } = useContext(NewTradeInContext);

    useEffect(() => {
        if ((!isShowroomV2Enabled || AccountService.isAuthenticated()) && !fieldsLocked) {
            setFieldsLocked(true);
        }
    }, []);

    const getDatePickerStartDate = () => {
        if (moment().hour() >= APPOINTMENTS_ENDING_TIME) {
            return moment().add(1, 'd');
        }
        return moment();
    };

    const onCancel = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_CANCEL_BTN());
        onPreviousStep();
    };

    const lang = LocaleService.getSimpleLocale();
    const [appointmentDate, setAppointmentDate] = useState(getDatePickerStartDate());
    const [appointmentTimes, setAppointmentTimes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [appointmentConfirmed, setAppointmentConfirmed] = useState(false);
    const [timeSelectionError, setTimeSelectionError] = useState(false);
    const [fetching, setFetching] = useState(false);

    // Scroll to the top
    useEffect(() => {
        const modal = document.getElementById('webauto-trade-in-modal');
        if (modal) setTimeout(() => modal.scrollIntoView({ behavior: 'smooth' }), 200);
        AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_APPOINTMENT());
    }, []);

    const createAppointmentLead = async (selectedAppointmentDate) => {
        // If no tradeIn vehicle found

        const noTradeInVehicle = !tradeInVehicle?.tradeVehicleToken && !accountTradeInVehicle?.id;

        if (noTradeInVehicle) {
            return false;
        }
        const {
            contact,
            make,
            model,
            trim,
            year,
            style,
            exteriorColor,
            interiorColor,
            odometer,
            details,
            vin,
        } = tradeInVehicle;

        const appointmentInfo = `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.LEAD_INFO_TRADE360', {
            date: selectedAppointmentDate,
            time: appointmentTimes.join(','),
            tradeInVehicle: tradeInVehicle?.name || accountTradeInVehicle?.vehicle?.vehicleId,
            amount: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.amount || accountTradeInVehicle?.amount,
            high: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.high || accountTradeInVehicle.maxAmount,
            low: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.low || accountTradeInVehicle.minAmount,
        })}`;

        const { firstname, lastname, email } = contact;

        const leadSubmissionBuilder = new LeadSubmissionBuilder(config, 'Appointment')
            .addContactInformation(firstname, lastname, undefined, email, undefined, '')
            .addTradeInVehicle({
                year: parseInt(year, 10),
                make,
                model,
                trim,
                bodyStyle: style,
                exteriorColor: exteriorColor?.description,
                interiorColor: interiorColor?.description,
                kilometers: parseInt(odometer, 10),
                details,
                vin,
            });
        leadSubmissionBuilder.addLeadType(LEAD_TYPES.APPOINTMENT_USED);

        if (!isEmpty(tradeInVehicle.estimation)) {
            const { amount, high, low, amountWasUpdated = true } = tradeInVehicle.estimation;
            leadSubmissionBuilder.addTradeInEstimation(
                parseInt(amount, 10),
                parseInt(low || amount, 10),
                parseInt(high || amount, 10),
                amountWasUpdated
            );
        }

        // Add extra info in basic info textbox in lead
        if (!isEmpty(selectedAppointmentDate)) {
            leadSubmissionBuilder.addBasicInfo(appointmentInfo);
        }

        if (!isEmpty(quote)) {
            leadSubmissionBuilder.addQuote(quote);
        }

        if (quote) {
            leadSubmissionBuilder.addVehicle(quote);
        } else if (vehicleInventoryId || catalogVehicleId) {
            leadSubmissionBuilder.addVehicleWithoutQuote(vehicleInventoryId, catalogVehicleId);
        } else {
            leadSubmissionBuilder.deleteVehicle();
        }

        leadSubmissionBuilder.setIsAccountCreation();

        return leadSubmissionBuilder.submit(false).then((response) => {
            const { leadId } = response;
            ShiftDigitalService.setDealerMake(config?.dealerMake);
            ShiftDigitalService.createTradeInAppointmentEvent(
                tradeInVehicle?.year,
                tradeInVehicle?.make,
                tradeInVehicle?.model,
                leadId
            );
            ShiftDigitalService.createFormEvent(leadId, 'appointment', true, null, false, quote?.vehicle, {
                year: tradeInVehicle?.year,
                make: tradeInVehicle?.make,
                model: tradeInVehicle?.model,
            });
        });
    };

    // Notify top tradeIn bar about the update
    /**
     *
     * @param event - TRADEIN_UPDATED or TRADEIN_CREATED
     */
    const dispatchTradeInEvent = (event) => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(event));
        }
    };

    // Checking if official evaluation has been already completed at this point
    // Use account tradeIn vehicle questions and answers
    const isEvaluationCompleted = () => {
        const officialEvaluationStatus = QuestionsListHelpers.getQuestionnaireProgressStatus(
            accountTradeInVehicle?.evaluationQuestion || {},
            Models.DeskingQuestionsModel
        );
        return officialEvaluationStatus === OFFICIAL_FORM_STATUS.ALL_COMPLETED;
    };

    /**
     * 1. Send an appointment lead
     * 2. To update trade-in with selected appointment date and time (if user is logged in)
     */
    const handleAppointment = async (event) => {
        event.preventDefault();
        if (isEmpty(appointmentTimes)) {
            setTimeSelectionError(true);
            return false;
        }

        setLoading(true);
        try {
            const selectedAppointmentDate = appointmentDate.format(APPOINTMENT_DATE_FORMAT);
            // To send an appointment lead
            await createAppointmentLead(selectedAppointmentDate);

            // Add selected appointment date to the trade-in
            // Due to technical limitation should not allow to update trade-in if account is not authenticated
            if (AccountService.isAuthenticated() && accountTradeInVehicle?.id) {
                const updatedTradeIn = accountTradeInVehicle.set('appointmentDate', selectedAppointmentDate);
                await TradeInService.saveTradeInNew(updatedTradeIn, quote, true);
                dispatchTradeInEvent(EVENTS.TRADEIN_UPDATED);
            }
            setAppointmentConfirmed(true);
            // Allow to close the confirmation modal without warning if official evaluation has been already completed
            onNextStep(!!isEvaluationCompleted());
        } catch (e) {
            setError(true);
            logger.error('Error fetching the profile information', e);
        }
        return setLoading(false);
    };

    // Save an appointment date on official evaluation
    const saveAppointmentDate = () => {
        onOfficialEvaluation(appointmentDate.format(APPOINTMENT_DATE_FORMAT));
    };

    // set selected appointment time
    const setTimeChange = (times) => {
        if (times.length > 0) {
            AnalyticsService.ga4Trigger(TAGS.TRADE_IN_APPOINTMENT_TIME_SELECTION(times.toString()));
        }
        setTimeSelectionError(false);
        setAppointmentTimes(times);
    };

    const setDateChange = (date) => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_APPOINTMENT_DATE_SELECTION(date.format(APPOINTMENT_DATE_FORMAT)));
        setAppointmentDate(date);
    };

    const saveTradeInIntoAccount = ({ existingAccount = false, onSaveCallback }) => {
        setFetching(true);
        const tradeInValues = {
            amount: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.amount || 0,
            high: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.high || 0,
            low: tradeInVehicle?.estimation?.tradeInExtraInfo?.estimation?.low || 0,
            taxesFactor: tradeInVehicle?.estimation?.taxesFactor || 0,
        };

        saveTradeIn({ existingAccount, tradeInValues })
            .then(() => {
                onSaveCallback();
            })
            .catch((e) => {
                this.logger.error('Error saving tradeIn to the account', e);
            })
            .finally(() => setFetching(false));
    };

    const tradeVehicleName = LocaleService.t('TRADE_IN.VEHICLE_NAME', {
        year: accountTradeInVehicle?.vehicle?.year || tradeInVehicle.year,
        make: accountTradeInVehicle?.vehicle?.make || tradeInVehicle.make,
        model: accountTradeInVehicle?.vehicle?.model || tradeInVehicle.model,
        modelTrim: accountTradeInVehicle?.vehicle?.modelTrim || tradeInVehicle.trim,
        modelTrimStyle: '',
    });

    const quoteVehicleInfo = quote?.vehicle?.getYearMakeModelTrim() || '';
    const confirmationDesc = isShowroomV2Enabled
        ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.CONFIRMATION_DESC')
        : null;

    return (
        <div className="appointment-form">
            {appointmentConfirmed ? (
                <ConfirmationMessageTrade360
                    title={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.CONFIRMATION_TITLE')}
                    description={!isEvaluationCompleted() && confirmationDesc}
                    closeText={closeText}
                    onClose={onClose}
                    confirmationType={CONFIRMATION_TYPE.APPOINTMENT}
                >
                    <TradeInCta
                        officialAnswers={accountTradeInVehicle?.evaluationQuestion}
                        onOfficialEvaluation={
                            isShowroomV2Enabled && !isEvaluationCompleted() ? saveAppointmentDate : null
                        }
                        saveTradeIn={saveTradeInIntoAccount}
                        fetching={fetching}
                    />
                </ConfirmationMessageTrade360>
            ) : (
                <form onSubmit={handleAppointment}>
                    <section className="appointment-form--header">
                        <div className="appointment-form--header--title">
                            <div className={WEB_CSS_CLASSNAMES.HEADING2}>
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.REQUEST_TITLE')}
                            </div>
                            {AccountService.isAuthenticated() &&
                                tradeInVehicle?.creationDate && (
                                    <div className={WEB_CSS_CLASSNAMES.BODY2}>
                                        {LocaleService.t('TRADE_IN.CREATED_ON', { date: tradeInVehicle.creationDate })}
                                    </div>
                                )}
                        </div>
                        {error && (
                            // Display an error box if lead creation or appraisal value request failed
                            <ErrorMessage />
                        )}
                        <div className="appointment-form--header--vehicles">
                            <VehicleTypeLabel
                                vehicleLabel={LocaleService.t('TRADE_IN.TITLES.YOUR_TRADEIN_VEHICLE')}
                                vehicleInfo={tradeVehicleName}
                                isVehicleBold
                            />
                            <VehicleTypeLabel
                                vehicleLabel={LocaleService.t('TRADE_IN.TITLES.VEHICLE_OF_INTEREST')}
                                vehicleInfo={quoteVehicleInfo}
                            />
                        </div>
                    </section>
                    <section className="appointment-form--schedule">
                        <span className={`${WEB_CSS_CLASSNAMES.SUBHEADING1} appointment-form--labels`}>
                            {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.SCHEDULE_DESC')}
                        </span>
                        <div className="appointment-form--schedule-wrapper">
                            <div className="appointment-form--schedule-wrapper--date">
                                <DatePicker
                                    onDatePickerChange={(date) => {
                                        onChange();
                                        setDateChange(date);
                                        setAppointmentTimes([]);
                                    }}
                                    maxDate={moment().add(3, 'month')}
                                    minDate={getDatePickerStartDate()}
                                    locale={lang.toLowerCase()}
                                    selected={getDatePickerStartDate()}
                                    inline
                                />
                            </div>
                            <div className="appointment-form--schedule-wrapper--time">
                                <TimePicker
                                    forDate={appointmentDate.unix() * 1000}
                                    onTimePick={(times) => {
                                        onChange();
                                        setTimeChange(times);
                                    }}
                                    selectedTimes={appointmentTimes}
                                    min={APPOINTMENTS_STARTING_TIME}
                                    max={APPOINTMENTS_ENDING_TIME}
                                />
                            </div>
                        </div>
                        <div className="clearfix appointment-form--schedule--time-card-error">
                            {timeSelectionError && (
                                <p className="error-input">
                                    {LocaleService.t(
                                        'TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.ERROR_APPOINTMENT_TIME'
                                    )}
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="clearfix vehicle-buttons-container" data-test="trade-in.vehicle-form.submit">
                        <div className="vehicle-buttons">
                            <ActionButton noPrefix noArrow onClick={onCancel} loading={loading}>
                                {LocaleService.t('TRADE_IN.BUTTONS.CANCEL')}
                            </ActionButton>
                            <ActionButton next noPrefix noArrow onClick={onNextStep} loading={loading}>
                                {LocaleService.t('TRADE_IN.BUTTONS.SUBMIT_PREFERENCE')}
                            </ActionButton>
                        </div>
                    </section>
                </form>
            )}
        </div>
    );
};

AppointmentFormTrade360.defaultProps = {
    config: {},
};

AppointmentFormTrade360.propTypes = {
    tradeInVehicle: PropTypes.object,
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
    onPreviousStep: PropTypes.func,
    onNextStep: PropTypes.func,
    onClose: PropTypes.func,
    closeText: PropTypes.string,
    accountTradeInVehicle: PropTypes.object, // trade vehicle from global state
    onOfficialEvaluation: PropTypes.func,
    onChange: PropTypes.func,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    config: PropTypes.object,
};

AppointmentFormTrade360.displayName = 'AppointmentFormTrade360';

function mapStateToProps(state) {
    return {
        accountTradeInVehicle: state.tradeInVehicle,
    };
}

export default connect(
    mapStateToProps,
    null
)(AppointmentFormTrade360);
